import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      if (response.data.authenticated) {
        setUser(response.data.user);
      } else {
        localStorage.removeItem('authToken');
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('authToken');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (gebruikersnaam, wachtwoord) => {
    // Security warning: Check if logging in with default admin/admin
    const isDefaultAdmin = gebruikersnaam.toLowerCase() === 'admin' && wachtwoord === 'admin';
    
    try {
      const response = await api.post('/auth/login', { gebruikersnaam, wachtwoord });
      if (response.data.success && response.data.token) {
        // Store JWT token in localStorage
        localStorage.setItem('authToken', response.data.token);
        setUser(response.data.user);
        
        // Show security warning if using default credentials
        if (isDefaultAdmin) {
          setTimeout(() => {
            alert('⚠️ VEILIGHEIDSWAARSCHUWING!\n\nJe gebruikt het standaard admin/admin wachtwoord.\n\nDit is zeer onveilig voor een online applicatie!\n\nWijzig je wachtwoord DIRECT via:\nGebruikers → Admin gebruiker → Wachtwoord wijzigen');
          }, 500);
        }
        
        return { success: true, isDefaultAdmin };
      }
      return { success: false, error: 'Login mislukt' };
    } catch (error) {
      if (!error.response) {
        return {
          success: false,
          error:
            'Geen contact met de server (Railway). Wacht op deploy of controleer of de backend draait.',
        };
      }
      if (error.response.status === 404) {
        return {
          success: false,
          error: 'Login-API niet bereikbaar (404). Railway-deploy faalt mogelijk — check deploy-logs.',
        };
      }
      if (error.response.status === 503) {
        return {
          success: false,
          error: error.response?.data?.error || 'Database nog niet klaar — probeer over 1 minuut opnieuw.',
        };
      }
      return {
        success: false,
        error: error.response?.data?.error || 'Login mislukt',
      };
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Remove JWT token from localStorage
      localStorage.removeItem('authToken');
      setUser(null);
    }
  };

  const isAdmin = () => {
    return user && user.rol === 'admin';
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAdmin,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
